import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { API_URL } from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { 
  Sheet, 
  SheetContent, 
  SheetHeader, 
  SheetTitle, 
  SheetTrigger 
} from '../components/ui/sheet';
import { 
  GraduationCap, 
  Clock, 
  MapPin, 
  Check, 
  X,
  Users,
  Euro,
  Calendar
} from 'lucide-react';
import { useTranslation } from '../contexts/LanguageContext';
import { format } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';

const GroupClassInvitationsSheet = () => {
  const navigate = useNavigate();
  const { language } = useTranslation();
  const [open, setOpen] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [respondingTo, setRespondingTo] = useState(null);

  useEffect(() => {
    if (open) {
      loadInvitations();
    }
  }, [open]);

  // Load invitations when sheet opens (no polling)
  useEffect(() => {
    if (open) {
      loadInvitations();
    }
  }, [open]);

  const loadInvitations = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/group-classes/invitations/pending`, { 
        withCredentials: true 
      });
      setInvitations(data.invitations || []);
    } catch (error) {
      console.error('Error loading group class invitations:', error);
    } finally {
      setLoading(false);
    }
  };

  const respondToInvitation = async (classId, accept) => {
    setRespondingTo(classId);
    try {
      if (accept) {
        await api.post(
          `${API_URL}/group-classes/${classId}/register`,
          {},
          { withCredentials: true }
        );
        toast.success(
          language === 'fr' 
            ? 'Vous êtes inscrit au cours !' 
            : 'You are registered for the class!'
        );
      } else {
        await api.post(
          `${API_URL}/group-classes/invitations/${classId}/decline`,
          {},
          { withCredentials: true }
        );
        toast.success(
          language === 'fr' 
            ? 'Invitation déclinée' 
            : 'Invitation declined'
        );
      }
      
      // Refresh invitations
      loadInvitations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setRespondingTo(null);
    }
  };

  const getLevelBadge = (level) => {
    const levels = {
      all: { label: language === 'fr' ? 'Tous niveaux' : 'All levels', color: 'bg-blue-500/20 text-blue-400' },
      beginner: { label: language === 'fr' ? 'Débutant' : 'Beginner', color: 'bg-green-500/20 text-green-400' },
      intermediate: { label: language === 'fr' ? 'Intermédiaire' : 'Intermediate', color: 'bg-yellow-500/20 text-yellow-400' },
      advanced: { label: language === 'fr' ? 'Avancé' : 'Advanced', color: 'bg-red-500/20 text-red-400' }
    };
    const levelInfo = levels[level] || levels.all;
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${levelInfo.color}`}>
        {levelInfo.label}
      </span>
    );
  };

  const formatClassDate = (dateString, startTime) => {
    if (!dateString) {
      return startTime ? `${language === 'fr' ? 'À' : 'At'} ${startTime}` : '';
    }
    try {
      const date = new Date(dateString);
      return format(date, "EEEE d MMMM", { locale: language === 'fr' ? fr : enUS }) + 
             (startTime ? ` ${language === 'fr' ? 'à' : 'at'} ${startTime}` : '');
    } catch {
      return startTime || '';
    }
  };

  const pendingCount = invitations.length;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          data-testid="group-class-invitations-btn"
          className="relative theme-text hover:theme-accent"
        >
          <GraduationCap size={24} />
          {pendingCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#10B981] rounded-full flex items-center justify-center text-[10px] font-bold text-white animate-pulse">
              {pendingCount > 9 ? '9+' : pendingCount}
            </span>
          )}
        </Button>
      </SheetTrigger>
      
      <SheetContent side="bottom" className="theme-bg border-t theme-border h-[80vh] rounded-t-3xl">
        <SheetHeader className="pb-4 border-b theme-border">
          <SheetTitle className="theme-text flex items-center gap-2">
            <GraduationCap size={20} className="text-[#10B981]" />
            {language === 'fr' ? 'Invitations Cours Collectifs' : 'Group Class Invitations'}
            {pendingCount > 0 && (
              <span className="px-2 py-0.5 bg-[#10B981] rounded-full text-xs text-white">
                {pendingCount}
              </span>
            )}
          </SheetTitle>
        </SheetHeader>
        
        <div className="mt-4 space-y-3 overflow-y-auto max-h-[calc(80vh-100px)]">
          {loading ? (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-2 border-[#10B981] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="theme-text-secondary">
                {language === 'fr' ? 'Chargement...' : 'Loading...'}
              </p>
            </div>
          ) : invitations.length === 0 ? (
            <div className="text-center py-12">
              <GraduationCap size={48} className="mx-auto text-zinc-700 mb-4" />
              <p className="theme-text-secondary">
                {language === 'fr' ? 'Aucune invitation en attente' : 'No pending invitations'}
              </p>
              <Button
                onClick={() => {
                  setOpen(false);
                  navigate('/group-classes');
                }}
                variant="outline"
                className="mt-4 theme-border"
              >
                {language === 'fr' ? 'Découvrir les cours' : 'Browse classes'}
              </Button>
            </div>
          ) : (
            invitations.map((invitation) => (
              <div
                key={invitation.id}
                className="theme-bg-card border theme-border rounded-xl p-4 space-y-3"
                data-testid={`group-class-invitation-${invitation.id}`}
              >
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-bold theme-text">{invitation.title}</h3>
                    <p className="text-sm theme-text-secondary">
                      {language === 'fr' ? 'Par' : 'By'} {invitation.instructor}
                    </p>
                  </div>
                  {getLevelBadge(invitation.level)}
                </div>

                {/* Details */}
                <div className="flex flex-wrap gap-3 text-sm">
                  <div className="flex items-center gap-1 theme-text-secondary">
                    <Calendar size={14} />
                    <span>{formatClassDate(invitation.date, invitation.startTime)}</span>
                  </div>
                  {invitation.clubName && (
                    <div className="flex items-center gap-1 theme-text-secondary">
                      <MapPin size={14} />
                      <span>{invitation.clubName}</span>
                    </div>
                  )}
                </div>

                {/* Time & Price */}
                <div className="flex flex-wrap gap-3 text-sm">
                  <div className="flex items-center gap-1 theme-text-secondary">
                    <Clock size={14} />
                    <span>{invitation.startTime} - {invitation.endTime}</span>
                  </div>
                  <div className="flex items-center gap-1 theme-text-secondary">
                    <Euro size={14} />
                    <span>{invitation.price}€</span>
                  </div>
                  <div className="flex items-center gap-1 theme-text-secondary">
                    <Users size={14} />
                    <span>
                      {invitation.currentParticipants || 0}/{invitation.maxParticipants} 
                      {language === 'fr' ? ' places' : ' spots'}
                    </span>
                  </div>
                </div>

                {/* Description */}
                {invitation.description && (
                  <p className="text-sm text-zinc-400 line-clamp-2">{invitation.description}</p>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-2">
                  <Button
                    onClick={() => respondToInvitation(invitation.id, true)}
                    disabled={respondingTo === invitation.id}
                    className="flex-1 bg-[#10B981] text-white hover:bg-[#059669]"
                    data-testid={`accept-group-class-${invitation.id}`}
                  >
                    <Check size={16} className="mr-1" />
                    {respondingTo === invitation.id 
                      ? '...' 
                      : (language === 'fr' ? "S'inscrire" : 'Register')
                    }
                  </Button>
                  <Button
                    onClick={() => respondToInvitation(invitation.id, false)}
                    disabled={respondingTo === invitation.id}
                    variant="outline"
                    className="flex-1 theme-border text-zinc-300 hover:theme-bg-card"
                    data-testid={`decline-group-class-${invitation.id}`}
                  >
                    <X size={16} className="mr-1" />
                    {language === 'fr' ? 'Décliner' : 'Decline'}
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default GroupClassInvitationsSheet;
